import {
  GARAGE_FLAGS,
  type GarageFlag,
  type InstallationReport,
  RECENCY_DAYS,
} from "@bt/domain/report";
import { ExternalLinkIcon, FlagIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import type { Column, Row } from "../charts/frame";
import { useAdminFormat } from "../format";
import { Grid, HistoryFigure, ReportView, Section, SortableTable, TallyFigure } from "./kit";

type UserFilter = "never-back" | "quiet" | "invited";

/** What a garage can be an outlier in (`outlier:<measure>`). */
const OUTLIER_MEASURES = [
  "parts",
  "vehicles",
  "work_orders",
  "file_bytes",
  "active_days",
  "requests",
];

/** Every garage, and garages and people from created to gone quiet (figures.md › Garages and people). */
export function GaragesPage() {
  const { t } = useTranslation();
  return (
    <div className="space-y-8">
      <Section title={t("adminApp.garages.table")} description={t("adminApp.garages.tableHint")}>
        <ReportView>{(report) => <GarageTable report={report} />}</ReportView>
      </Section>
      <Section
        title={t("adminApp.garages.lifecycle")}
        description={t("adminApp.garages.lifecycleHint")}
      >
        <ReportView>{(report) => <Lifecycle report={report} />}</ReportView>
        <Grid>
          <HistoryFigure
            title={t("adminApp.garages.garagesOverTime")}
            figures={[
              { figure: "garages.unclaimed", label: t("adminApp.garages.unclaimed") },
              { figure: "garages.neverOpened", label: t("adminApp.garages.neverOpened") },
              { figure: "garages.stalled", label: t("adminApp.garages.stalled") },
              { figure: "garages.dormant", label: t("adminApp.garages.dormant") },
            ]}
            csvName="garage-lifecycle"
          />
          <HistoryFigure
            title={t("adminApp.garages.peopleOverTime")}
            figures={[
              { figure: "users.invitationsOpen", label: t("adminApp.garages.invited") },
              { figure: "users.neverBack", label: t("adminApp.garages.neverBack") },
              { figure: "users.goneQuiet", label: t("adminApp.garages.goneQuiet") },
            ]}
            csvName="people-lifecycle"
          />
        </Grid>
      </Section>
      <Section
        title={t("adminApp.garages.recency")}
        description={t("adminApp.garages.recencyHint")}
      >
        <ReportView>
          {(report) => (
            <Grid>
              <Recency
                title={t("adminApp.garages.garageRecency")}
                recency={report.garages?.recency}
                csvName="garage-recency"
              />
              <Recency
                title={t("adminApp.garages.peopleRecency")}
                recency={report.users?.recency}
                csvName="people-recency"
              />
            </Grid>
          )}
        </ReportView>
        <Grid>
          <HistoryFigure
            title={t("adminApp.garages.activeGarages")}
            figures={[
              { figure: "garages.active7", label: t("adminApp.garages.last7") },
              { figure: "garages.active30", label: t("adminApp.garages.last30") },
            ]}
            csvName="active-garages"
          />
          <HistoryFigure
            title={t("adminApp.garages.activePeople")}
            figures={[
              { figure: "users.active7", label: t("adminApp.garages.last7") },
              { figure: "users.active30", label: t("adminApp.garages.last30") },
            ]}
            csvName="active-people"
          />
        </Grid>
      </Section>
    </div>
  );
}

/** A flag's label: a lifecycle flag, or `outlier:<measure>`. */
function useFlagLabel() {
  const { t } = useTranslation();
  return (flag: string) => {
    if ((GARAGE_FLAGS as readonly string[]).includes(flag)) {
      return t(`adminApp.flags.${flag as GarageFlag}`);
    }
    if (flag.startsWith("outlier:")) {
      const measure = flag.slice("outlier:".length);
      return t("adminApp.flags.outlier", {
        measure: OUTLIER_MEASURES.includes(measure)
          ? t(`adminApp.flags.measures.${measure as "parts"}`)
          : measure,
      });
    }
    return flag;
  };
}

function GarageTable({ report }: { report: InstallationReport }) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  const flagLabel = useFlagLabel();
  if (!report.garageTable) {
    return <p className="text-sm text-muted-foreground">{t("adminApp.garages.noTable")}</p>;
  }
  const count = (key: string, label: string): Column => ({
    key,
    label,
    numeric: true,
    format: (v) => format.count(Number(v)),
  });
  const columns: Column[] = [
    { key: "name", label: t("adminApp.garages.name") },
    { key: "createdOn", label: t("adminApp.garages.createdOn") },
    count("members", t("adminApp.garages.members")),
    count("parts", t("adminApp.usage.records.parts")),
    count("vehicles", t("adminApp.usage.records.vehicles")),
    count("workOrders", t("adminApp.usage.records.workOrders")),
    count("orders", t("adminApp.usage.records.orders")),
    count("breadth", t("adminApp.garages.breadth")),
    count("activeDays30", t("adminApp.garages.activeDays")),
    count("requests30", t("adminApp.garages.requests")),
    { key: "lastActiveOn", label: t("adminApp.garages.lastActive") },
    { key: "flags", label: t("adminApp.garages.flags") },
  ];
  const rows: Row[] = report.garageTable.map((g) => ({
    name: g.name ?? g.id,
    createdOn: g.createdOn ?? null,
    members: g.members ?? 0,
    parts: g.parts ?? 0,
    vehicles: g.vehicles ?? 0,
    workOrders: g.workOrdersTotal ?? 0,
    orders: g.orders ?? 0,
    breadth: g.breadth ?? 0,
    activeDays30: g.activeDays30 ?? 0,
    requests30: g.requests30 ?? 0,
    lastActiveOn: g.lastActiveOn ?? null,
    flags: (g.flags ?? []).map(flagLabel).join(", "),
  }));
  const flagsOf = new Map(report.garageTable.map((g) => [g.name ?? g.id, g.flags ?? []]));
  return (
    <SortableTable
      title={t("adminApp.garages.tableTitle", { count: rows.length })}
      description={t("adminApp.garages.tableFigureHint")}
      columns={columns}
      rows={rows}
      csvName="garages"
      initialSort="requests30"
      limit={25}
      render={(key, row): ReactNode => {
        if (key === "lastActiveOn" && row.lastActiveOn === null) return t("adminApp.garages.never");
        if (key !== "flags") return undefined;
        const flags = flagsOf.get(String(row.name)) ?? [];
        return (
          <span className="flex flex-wrap gap-1">
            {flags.map((flag) => (
              <Badge key={flag} tone="outline">
                <FlagIcon aria-hidden />
                {flagLabel(flag)}
              </Badge>
            ))}
          </span>
        );
      }}
    />
  );
}

function Lifecycle({ report }: { report: InstallationReport }) {
  const { t } = useTranslation();
  const format = useAdminFormat();
  const lifecycle = report.garages?.lifecycle;
  const users = report.users;
  const base = report.meta.publicUrl ? report.meta.publicUrl.replace(/\/$/, "") : null;
  const link = (filter: UserFilter) =>
    base ? (
      <a
        href={`${base}/admin/users?filter=${filter}`}
        target="_blank"
        rel="noreferrer"
        className="font-medium text-brand-fg hover:underline"
      >
        {t("adminApp.garages.showPeople")}
        <ExternalLinkIcon className="ml-1 inline size-3.5 align-[-2px]" aria-hidden />
      </a>
    ) : null;
  const tile = (key: string, label: string, value: number | undefined, detail?: ReactNode) => (
    <Card key={key} className="p-3">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold tracking-tight tabular">{format.count(value)}</p>
      {detail ? <div className="mt-1 text-xs text-muted-foreground">{detail}</div> : null}
    </Card>
  );
  return (
    <div className="space-y-3">
      <h3 className="text-sm font-medium">{t("adminApp.garages.garages")}</h3>
      <div className="grid grid-cols-4 gap-3">
        {tile(
          "unclaimed",
          t("adminApp.garages.unclaimed"),
          lifecycle?.unclaimed,
          t("adminApp.garages.unclaimedHint"),
        )}
        {tile(
          "neverOpened",
          t("adminApp.garages.neverOpened"),
          lifecycle?.neverOpened,
          t("adminApp.garages.neverOpenedHint"),
        )}
        {tile(
          "stalled",
          t("adminApp.garages.stalled"),
          lifecycle?.stalled,
          t("adminApp.garages.stalledHint"),
        )}
        {tile(
          "dormant",
          t("adminApp.garages.dormant"),
          lifecycle?.dormant,
          t("adminApp.garages.dormantHint"),
        )}
      </div>
      <h3 className="text-sm font-medium">{t("adminApp.garages.people")}</h3>
      <div className="grid grid-cols-4 gap-3">
        {tile(
          "invited",
          t("adminApp.garages.invited"),
          users?.invitations?.open,
          <>
            <p>
              {t("adminApp.garages.invitedHint", {
                after7: format.count(users?.invitations?.openAfter7Days),
                expired: format.count(users?.invitations?.expiredUnaccepted),
              })}
            </p>
            {link("invited")}
          </>,
        )}
        {tile(
          "neverBack",
          t("adminApp.garages.neverBack"),
          users?.activeOnlyOnJoinDay,
          <>
            <p>{t("adminApp.garages.neverBackHint")}</p>
            {link("never-back")}
          </>,
        )}
        {tile(
          "goneQuiet",
          t("adminApp.garages.goneQuiet"),
          users?.goneQuiet,
          <>
            <p>{t("adminApp.garages.goneQuietHint")}</p>
            {link("quiet")}
          </>,
        )}
      </div>
    </div>
  );
}

/** The recency buckets now: within 7, 30, 90, 180 and 365 days, over a year, never. */
function Recency({
  title,
  recency,
  csvName,
}: {
  title: string;
  recency: NonNullable<InstallationReport["garages"]>["recency"];
  csvName: string;
}) {
  const { t } = useTranslation();
  const label = (i: number) =>
    i < RECENCY_DAYS.length
      ? t("adminApp.garages.within", { count: RECENCY_DAYS[i] })
      : t("adminApp.garages.overAYear");
  const rows = [
    ...(recency?.buckets ?? []).map((count, i) => ({ value: label(i), count })),
    { value: t("adminApp.garages.never"), count: recency?.never ?? 0 },
  ];
  return (
    <TallyFigure
      title={title}
      description={
        recency?.beforeTracking
          ? t("adminApp.garages.beforeTracking", { count: recency.beforeTracking })
          : undefined
      }
      rows={rows}
      csvName={csvName}
    />
  );
}
